/**
 * A module's tab on another module's record (ADR-0251).
 *
 * A module declares, in its manifest's `frontend.record_tabs`, the tabs it puts
 * on other modules' records, and exports their bodies as `recordTabs` from its
 * frontend `index.ts`. The generated registry carries both. A host record page
 * (customers' story page, say) calls `useContributedTabs("Customer", id)` and
 * appends what comes back after its own tabs, rendering each body as
 * `<Body entityType id />`. The host never names the contributor: it imports
 * nothing from another module, and when the business has the contributing
 * module switched off, or the person lacks the tab's permission, the tab is
 * simply not returned (briefing §4: independence is behavioural).
 *
 * The registry and the session reach the hook through `ContributedTabsProvider`,
 * which the shell mounts with the generated `MODULES` and the signed-in
 * session. Without a provider the hook returns nothing, so a host rendered in
 * isolation (a unit test, a story) shows exactly its own tabs.
 *
 * Types here are structural on purpose: `@ken/sdk` does not depend on the
 * cockpit's registry types, and a test can hand it plain objects.
 */

import {
  createContext,
  createElement,
  useContext,
  useMemo,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from "react";

/** The props a contributed body receives: the record's identity, nothing else. */
export interface ContributedTabBodyProps {
  entityType: string;
  id: string;
}

/** One `frontend.record_tabs` manifest entry, as the browser receives it. */
export interface ContributedTabEntry {
  readonly entity_type: string;
  readonly key: string;
  readonly label: string;
  readonly order: number;
  readonly permissions: readonly string[];
  readonly component: string;
}

/** The slice of a registered module this hook reads. */
export interface ContributingModule {
  readonly name: string;
  readonly manifest?: {
    readonly frontend?: { readonly record_tabs?: readonly ContributedTabEntry[] };
  };
  readonly recordTabs?: Readonly<Record<string, ComponentType<ContributedTabBodyProps>>>;
}

/** What a host renders for one contributed tab. */
export interface ContributedTab {
  key: string;
  label: string;
  order: number;
  /** The contributing module — for diagnostics; a host must not branch on it. */
  module: string;
  Body: ComponentType<ContributedTabBodyProps>;
}

export interface ContributedTabsProviderProps {
  /** The generated registry (`MODULES`). */
  modules: readonly ContributingModule[];
  /** The business's `enabled_modules`. */
  enabledModules: readonly string[];
  /** The person's effective permissions. */
  permissions: readonly string[] | ReadonlySet<string>;
  children?: ReactNode;
}

interface Candidate extends ContributedTab {
  entityType: string;
}

/** Entity type -> its contributed tabs, already gated and ordered. */
type TabIndex = ReadonlyMap<string, readonly ContributedTab[]>;

const EMPTY: readonly ContributedTab[] = Object.freeze([]);
const NO_TABS: TabIndex = new Map();

const ContributedTabsContext = createContext<TabIndex>(NO_TABS);

/**
 * Gate every module's declared tabs by the business's modules and the person's
 * permissions, and index them by record type. Pure; exported for the shell's
 * own tests.
 */
export function indexContributedTabs(
  modules: readonly ContributingModule[],
  enabledModules: readonly string[],
  permissions: readonly string[] | ReadonlySet<string>,
): TabIndex {
  const enabled = new Set(enabledModules);
  const held: ReadonlySet<string> =
    permissions instanceof Set ? permissions : new Set(permissions as readonly string[]);
  const found: Candidate[] = [];
  for (const mod of modules) {
    if (!enabled.has(mod.name)) continue;
    for (const entry of mod.manifest?.frontend?.record_tabs ?? []) {
      // An entry with no permission is refused by the manifest law; refuse it
      // here too rather than show it to everybody.
      if (entry.permissions.length === 0) continue;
      if (!entry.permissions.every((p) => held.has(p))) continue;
      const Body = mod.recordTabs?.[entry.component];
      // The build refuses an unexported component; a missing body here means a
      // stale registry, and a blank tab is worse than none.
      if (!Body) continue;
      found.push({
        entityType: entry.entity_type,
        key: entry.key,
        label: entry.label,
        order: entry.order,
        module: mod.name,
        Body,
      });
    }
  }
  found.sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
  const index = new Map<string, ContributedTab[]>();
  for (const { entityType, ...tab } of found) {
    const list = index.get(entityType) ?? [];
    list.push(tab);
    index.set(entityType, list);
  }
  return index;
}

/** Mounted by the shell with the generated registry and the session. */
export function ContributedTabsProvider({
  modules,
  enabledModules,
  permissions,
  children,
}: ContributedTabsProviderProps): ReactElement {
  const value = useMemo(
    () => indexContributedTabs(modules, enabledModules, permissions),
    [modules, enabledModules, permissions],
  );
  return createElement(ContributedTabsContext.Provider, { value }, children);
}

/**
 * The tabs other modules contribute to this record, sorted by `order`. The
 * host renders each after its own tabs as `<host>-story-tab-<key>`, with the
 * body as `<Body entityType={entityType} id={id} />`.
 *
 * `id` is taken so the call names the record it is for; the gating does not
 * depend on it (a tab is per record type, not per record).
 */
export function useContributedTabs(
  entityType: string,
  id: string,
): readonly ContributedTab[] {
  void id;
  const index = useContext(ContributedTabsContext);
  return index.get(entityType) ?? EMPTY;
}
